import React, { useState, useCallback } from 'react';
import { DistributiveOmit } from 'react-redux';
import { SelectChangeEvent, Typography, Box, styled } from '@mui/material';
import { RadioOption } from 'components/RadioGroup/RadioGroup';
import { CheckboxOption } from 'components/CheckboxGroup/CheckboxGroup';
import { AssessmentsQuestionProps } from 'features/assessments/types';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import {
  ASSESSMENTS_MAX_DATE,
  ASSESSMENTS_DEFAULT_ANSWER_PLACEHOLDER,
  ASSESSMENTS_VALIDATION_ERRORS,
} from 'features/assessments/constants';
import { isIOSDevice } from 'utils/deviceInfoUtils';
import { validateSingleField } from 'features/assessments/utils/assessmentsAnswersValidation.utils';
import { decodeAndSanitizeAssessmentHtml } from 'utils/contentUtils';
import { DynamicFormField } from '../DynamicFormField/DynamicFormField';
import { RequiredAsterisk } from './AssessmentsRequiredAsterisk';

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
}));

type AssessmentsQuestionFieldProps = DistributiveOmit<AssessmentsQuestionProps, 'questionText'> & {
  questionLabelId: string;
};

export const AssessmentsQuestionField: React.FC<AssessmentsQuestionFieldProps> = (props) => {
  const { id, type, questionLabelId, placeholder, name } = props;
  const resolvedPlaceholder = placeholder || ASSESSMENTS_DEFAULT_ANSWER_PLACEHOLDER;
  const [validationError, setValidationError] = useState<string>('');

  const { theme } = useThemeUtils();

  const handleValidation = useCallback(
    (value: any) => {
      const validation = validateSingleField(props, value);
      setValidationError(validation.isValid ? '' : validation.errorMessage || '');
    },
    [props],
  );

  const handleDateValidation = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (event.target.validity.badInput) {
        setValidationError(ASSESSMENTS_VALIDATION_ERRORS.DATE_INVALID);
        return;
      }
      handleValidation(event.target.value);
    },
    [handleValidation],
  );

  const handleFocus = useCallback(() => {
    // Clear error on focus for better UX
    if (validationError) {
      setValidationError('');
    }
  }, [validationError]);

  switch (type) {
    case 'textbox': {
      const { value, onChange } = props;
      const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        // Apply max length constraint
        const maxLength = resolvedPlaceholder.toLowerCase().includes('email') ? 100 : 255;
        if (newValue.length <= maxLength) {
          onChange(newValue);
        }
      };
      const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        handleValidation(event.target.value);
      };
      const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Prevent Enter from submitting a wrapping <form>; this is a single-line
        // answer field and shouldn't trigger form submission.
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      };

      return (
        <Box>
          <DynamicFormField
            type="textbox"
            id={`assessment-answer__textbox-${id}`}
            name={name}
            value={value}
            placeholder={resolvedPlaceholder}
            onChange={handleTextFieldChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            error={!!validationError}
            slotProps={{
              htmlInput: {
                'aria-labelledby': questionLabelId,
                'aria-invalid': !!validationError,
                'aria-describedby': validationError ? `${id}-error` : undefined,
              },
            }}
          />
          {validationError && (
            <ErrorText id={`${id}-error`} role="alert">
              {validationError}
            </ErrorText>
          )}
        </Box>
      );
    }

    case 'textarea': {
      const { value, onChange } = props;
      const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(event.target.value);
      };
      const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        handleValidation(event.target.value);
      };

      return (
        <Box>
          <DynamicFormField
            type="textarea"
            id={`assessment-answer__textarea-${id}`}
            name={name}
            placeholder={resolvedPlaceholder}
            value={value}
            minRows={3}
            maxRows={6}
            onChange={handleTextAreaChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            error={!!validationError}
            slotProps={{
              input: { sx: { padding: 2 } },
              htmlInput: {
                'aria-labelledby': questionLabelId,
                'aria-invalid': !!validationError,
                'aria-describedby': validationError ? `${id}-error` : undefined,
              },
            }}
          />
          {validationError && (
            <ErrorText id={`${id}-error`} role="alert">
              {validationError}
            </ErrorText>
          )}
        </Box>
      );
    }

    case 'date': {
      const { value, onChange } = props;
      const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
        handleValidation(event.target.value);
      };
      const handleBlur = handleDateValidation;
      return (
        <Box>
          <DynamicFormField
            type="date"
            id={`assessment-answer__date-${id}`}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={handleDateChange}
            onBlur={handleBlur}
            error={!!validationError}
            slotProps={{
              htmlInput: {
                'aria-labelledby': questionLabelId,
                'aria-invalid': !!validationError,
                'aria-describedby': validationError ? `${id}-error` : undefined,
                // this refers to datefield component
                // and datefield component overrides all the props passed in it.
                // changing the behavior of datefield component
                // hence we are adding the max date and aria label here instead of in datefield component.
                max: ASSESSMENTS_MAX_DATE,
                ...(isIOSDevice() ? { role: 'textbox' } : {}),
              },
            }}
          />
          {validationError && (
            <ErrorText id={`${id}-error`} role="alert">
              {validationError}
            </ErrorText>
          )}
        </Box>
      );
    }

    case 'radio': {
      const { selectedOption, options, onChange } = props;
      const handleRadioChange = (option: RadioOption) => {
        if (option.value) {
          onChange(option.value);
        }
      };
      const sanitisedOptions: RadioOption[] = options.map(
        ({ value, label, children, isOptionRequired }) => ({
          value,
          label: isOptionRequired ? (
            <>
              {label}
              <RequiredAsterisk aria-hidden="true">*</RequiredAsterisk>
            </>
          ) : (
            label
          ),
          children,
        }),
      );

      return (
        <DynamicFormField
          type="radio"
          name={name}
          aria-labelledby={questionLabelId}
          selectedOption={selectedOption}
          options={sanitisedOptions}
          onOptionSelected={handleRadioChange}
          slotProps={{
            radioControl: {
              sx: {
                color: theme.palette.primary.main,
                py: '7px',
              },
            },
          }}
        />
      );
    }

    case 'checkbox': {
      const { selectedOptions, options, onChange } = props;
      const handleCheckboxToggle = (updatedOptions: CheckboxOption[]) => {
        const checked = updatedOptions
          .filter((option) => option.isChecked)
          .map((option) => option.value);
        onChange(checked);
      };
      // Explicit checkboxId/labelId because Checkbox.tsx derives a fallback id from `label`
      // via a template string, which breaks once label is a JSX node instead of a string.
      const optionsWithChecked: CheckboxOption[] = options.map(
        ({ value, label, children, isOptionRequired }) => ({
          value,
          label: isOptionRequired ? (
            <>
              {label}
              <RequiredAsterisk aria-hidden="true">*</RequiredAsterisk>
            </>
          ) : (
            label
          ),
          children,
          isChecked: selectedOptions.some((selected) => selected.value === value),
          checkboxId: `checkbox__${id}-${value}`,
          labelId: `checkbox__label-${id}-${value}`,
        }),
      );

      return (
        <DynamicFormField
          type="checkbox"
          name={name}
          options={optionsWithChecked}
          onToggle={handleCheckboxToggle}
          slotProps={{
            formControl: {
              'aria-labelledby': questionLabelId,
              sx: {
                width: '100%',
              },
            },
            checkbox: {
              sx: {
                py: '7px',
              },
            },
          }}
        />
      );
    }

    case 'select': {
      const { value, options, onChange } = props;

      const handleSelectChange = (event: SelectChangeEvent<unknown>) => {
        onChange(event.target.value as string);
      };

      const optionsWithPlaceholder = placeholder
        ? [{ value: '', label: placeholder, disabled: true }, ...options]
        : options;

      const renderValue = (selectedValue: unknown) => {
        if (!selectedValue) {
          return <Typography color="textDisabled">{placeholder}</Typography>;
        }

        const selectedOption = options.find((option) => option.value === selectedValue);

        return <Typography>{selectedOption?.label}</Typography>;
      };

      return (
        <DynamicFormField
          type="select"
          id={`assessment-answer__select-${id}`}
          labelId={questionLabelId}
          displayEmpty
          placeholder={placeholder}
          name={name}
          options={optionsWithPlaceholder}
          value={value}
          renderValue={renderValue}
          onChange={handleSelectChange}
          aria-labelledby={questionLabelId}
        />
      );
    }

    case 'label': {
      return (
        <Typography
          id={String(id)}
          variant="subheadline"
          color="grey.700"
          dangerouslySetInnerHTML={{ __html: decodeAndSanitizeAssessmentHtml(placeholder ?? '') }}
        />
      );
    }

    case 'unsupported': {
      return (
        <Typography id={String(id)} variant="subheadline" color="grey.700">
          {placeholder}
        </Typography>
      );
    }

    default:
      return null;
  }
};
